import { useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import Logo from "assets/icons/camerito.svg?react";
import { Box, Button, Flex, Heading, Spinner } from "@/ui";
import { getIsReady } from "@/store/app/selectors";
import { useControls } from "@/hooks";
import { useApp } from "@/store/app";
import { t } from "@/utils";
import { useBreakpointValue } from "@chakra-ui/react";
import getCameritoWebUrl from "@/utils/getCameritoWebUrl";
import { useStation } from "camera/store/station";
import { usePairingCode } from "camera/store/pairingCode";
import { getIsPaired } from "camera/store/station/selectors";
import PairingCodeFlow from "./PairingCodeFlow/PairingCodeFlow";

export default function PairingCode() {
  const isPaired = useStation(getIsPaired);
  const isReady = useApp(getIsReady);
  const shouldRender = !isPaired;
  const helpFlow = useControls();

  useEffect(() => {
    if (!shouldRender || !isReady) return;
    const store = usePairingCode.getState();
    if (!store.isValid()) store.getPairingCode();
  }, [shouldRender, isReady]);

  const { pairingCode } = usePairingCode(({ pairingCode }) => ({ pairingCode }));
  const code = pairingCode ? Array.from(pairingCode.code) : null;
  const qrSize = useBreakpointValue([120, 180, 210]);
  const logoSize = useBreakpointValue(["40px", null, "56px"]);

  if (!shouldRender) return null;

  return (
    <>
      <Flex
        position="absolute"
        inset={0}
        direction="column"
        align="center"
        borderRadius="24px"
        overflow="hidden"
        zIndex={2}
        bg="rgba(0,0,0,0.4)"
        sx={{ backdropFilter: "blur(18px)" }}
      >
        <Flex grow={1} borderColor="blackAlpha.200" flexDirection="column" justifyContent="center" alignItems="center">
          <Flex grow={1} flexDirection="column" justifyContent="center" alignItems="center">
            <Box position="relative" p={[8, 16]} bg="neutral.99" borderRadius="1rem">
              <QRCodeCanvas value={`${getCameritoWebUrl()}/pairing/${code?.join("")}`} size={qrSize} level="Q" />
              <Box
                pos="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                borderRadius="16px"
                border="4px solid"
                borderColor="neutral.99"
                bg="neutral.99"
              >
                <Logo width={logoSize} height={logoSize} />
              </Box>
            </Box>
            <Flex justify="center" opacity={0.7} mt="0.5rem">
              {code ? (
                code.map((number, i) => <CodeNumber key={i}>{number}</CodeNumber>)
              ) : (
                <Spinner color="neutral.99" />
              )}
            </Flex>
            <Heading as="h4" color="neutral.99" textAlign="center" maxW="296px" mt="1.5rem">
              {t("scanQrWithViewer")}
            </Heading>
          </Flex>
          <Button variant="text" color="neutral.99" onClick={helpFlow.open} justifySelf="flex-end" mb="1.25rem">
            {t("welcome.help.heading")}
          </Button>
        </Flex>
      </Flex>
      <PairingCodeFlow isOpen={helpFlow.isOpen} close={helpFlow.close} />
    </>
  );
}

function CodeNumber({ children }: { children: any }) {
  return (
    <Heading
      as="h4"
      fontWeight={600}
      fontSize="24px"
      lineHeight="32px"
      color="neutral.99"
      textAlign="center"
      sx={{
        ":nth-of-type(3n)": { mr: "5px" },
        ":last-of-type": { mr: "0px" }
      }}
    >
      {children}
    </Heading>
  );
}
